import { useEffect, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Download, ArrowRight, KeyRound, Copy, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function ApplySuccess() {
  const { id } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const [order, setOrder] = useState(loc.state?.order || null);
  const [showPw, setShowPw] = useState(false);
  const tempPassword = loc.state?.temp_password;
  const isNewUser = !!loc.state?.is_new_user;

  useEffect(() => {
    if (order) return;
    api.get(`/orders/${id}`).then((r) => setOrder(r.data.order)).catch(() => {
      toast.error("Order not found or session expired");
      nav("/");
    });
  }, [id, order, nav]);

  const downloadInvoice = async () => {
    const token = localStorage.getItem("w2e_token");
    const res = await fetch(`${API_BASE}/orders/${id}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast.error("Could not download invoice");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${order?.order_number || "invoice"}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyPw = () => {
    navigator.clipboard.writeText(tempPassword);
    toast.success("Password copied");
  };

  if (!order) return <PublicLayout><div className="p-10 text-slate-500">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="py-12 gradient-radial-blue">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="apply-success-card">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mx-auto">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="mt-4 font-display font-bold text-3xl sm:text-4xl text-[#0A192F] text-center">Payment successful!</h1>
            <p className="mt-2 text-center text-slate-600">Your order is confirmed. We've emailed you the details.</p>

            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Order number</div>
                  <div className="mt-0.5 font-mono font-bold text-lg text-[#0A192F]" data-testid="success-order-number">{order.order_number || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Amount</div>
                  <div className="mt-0.5 font-display font-bold text-lg text-[#0A192F]">₹{order.amount}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Service</div>
                  <div className="mt-0.5 text-[#0A192F]">{order.service_name}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-slate-500">Payment ID</div>
                  <div className="mt-0.5 font-mono text-xs text-slate-700 truncate">{order.cashfree_payment_id || order.cashfree_order_id}</div>
                </div>
                <div className="col-span-2 pt-3 border-t border-slate-200 grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Customer</div>
                    <div className="mt-0.5 text-[#0A192F]">{order.full_name}</div>
                    <div className="text-xs text-slate-500">{order.email}</div>
                    <div className="text-xs text-slate-500">+91 {order.mobile}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs uppercase tracking-widest text-slate-500">Status</div>
                    <div className="mt-0.5"><span className="status-badge badge-warn">Documents pending</span></div>
                  </div>
                </div>
              </div>
            </div>

            {isNewUser && tempPassword && (
              <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="apply-credentials">
                <div className="flex items-start gap-3">
                  <KeyRound className="w-5 h-5 text-amber-800 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="font-semibold text-amber-900">Your Way2Efiling account is ready</div>
                    <div className="text-xs text-amber-800 mt-1">We've auto-created your account so you can track this order. Save these credentials or reset your password from the dashboard.</div>
                    <div className="mt-3 grid gap-2 text-sm">
                      <div className="flex items-center justify-between bg-white rounded-md border border-amber-200 px-3 py-2">
                        <span className="text-slate-500 text-xs">Email</span>
                        <span className="font-mono text-[#0A192F]">{order.email}</span>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-md border border-amber-200 px-3 py-2">
                        <span className="text-slate-500 text-xs">Temporary password</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[#0A192F]" data-testid="apply-temp-password">{showPw ? tempPassword : "•".repeat(tempPassword.length)}</span>
                          <button onClick={() => setShowPw((s) => !s)} className="text-slate-500 hover:text-[#1E3A8A]">{showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
                          <button onClick={copyPw} className="text-slate-500 hover:text-[#1E3A8A]" data-testid="apply-copy-pw"><Copy className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => nav(`/dashboard/orders/${id}`)} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="apply-go-dashboard-btn">
                Upload documents <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button variant="outline" onClick={downloadInvoice} className="h-11" data-testid="apply-download-invoice-btn">
                <Download className="w-4 h-4 mr-1" /> Download invoice
              </Button>
              <Link to="/services">
                <Button variant="ghost" className="h-11">Browse more services</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
